import React from "react";
import { motion } from "framer-motion";
import {
  Crown,
  MapPin,
  CalendarBlank,
  Users,
  Storefront,
  ArrowUpRight,
  Sparkle,
} from "@phosphor-icons/react";
import { Countdown } from "@/components/Countdown";

const fmtRange = (start, end) => {
  try {
    const s = new Date(start).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const e = end
      ? new Date(end).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : new Date(start).toLocaleDateString("en-US", { year: "numeric" });
    return `${s} – ${e}`;
  } catch {
    return start;
  }
};

export const SpotlightBanner = ({ show, onOpen, onUpgrade }) => {
  const today = new Date().toISOString().slice(0, 10);
  if (!show || (show.end_date || show.start_date || "") < today) return null;
  const t = show.teaser || {};

  return (
    <section className="mx-auto max-w-7xl px-5 pt-14 sm:px-8" data-testid="spotlight-banner">
      <div className="mb-4 flex items-center gap-2">
        <Sparkle size={18} weight="fill" className="text-amber-300" />
        <h2 className="font-display text-lg font-bold tracking-tight text-white">
          Featured Show
        </h2>
        <span className="text-xs uppercase tracking-[0.16em] text-slate-500">
          Next premium show · countdown to opening day
        </span>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl border border-amber-400/30 bg-white/[0.03]"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative h-56 lg:h-auto">
            <img src={show.image_url} alt={show.name} className="h-full w-full object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0f111c] lg:bg-gradient-to-r" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f111c] via-transparent to-transparent lg:hidden" />
            <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-black/50 px-3 py-1.5 text-xs font-semibold text-amber-300 backdrop-blur-md">
              <Crown size={13} weight="fill" /> Featured Premium
            </span>
          </div>

          <div className="relative flex flex-col justify-center p-7 sm:p-9">
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-amber-400/10 blur-3xl" />
            <span className="text-xs font-semibold uppercase tracking-wider text-amber-300">{show.category}</span>
            <h3 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
              {show.name}
            </h3>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-300">
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={15} weight="fill" className="text-blue-400" /> {show.city}, {show.country}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CalendarBlank size={15} className="text-slate-500" /> {fmtRange(show.start_date, show.end_date)}
              </span>
            </div>

            {show.starts_at && <Countdown to={show.starts_at} />}

            <div className="mt-5 flex flex-wrap gap-3">
              {t.attendee_count != null && (
                <Stat icon={<Users size={16} className="text-amber-300" />} label="Attendees" value={`${t.attendee_count.toLocaleString()}+`} />
              )}
              {t.exhibitor_count != null && (
                <Stat icon={<Storefront size={16} className="text-amber-300" />} label="Exhibitors" value={`${t.exhibitor_count}+`} />
              )}
            </div>

            {t.top_brands?.length > 0 && (
              <p className="mt-4 text-sm text-slate-400">
                Featuring <span className="text-slate-200">{t.top_brands.join(", ")}</span> &amp; more
              </p>
            )}

            <div className="mt-7 flex flex-wrap gap-3">
              <button
                data-testid="spotlight-view-btn"
                onClick={() => onOpen(show)}
                className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-blue-500"
              >
                View Details <ArrowUpRight size={16} weight="bold" />
              </button>
              {show.locked && (
                <button
                  data-testid="spotlight-upgrade-btn"
                  onClick={onUpgrade}
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-6 py-3 text-sm font-semibold text-amber-300 transition-colors duration-200 hover:bg-amber-400/20"
                >
                  <Crown size={15} weight="fill" /> Unlock Full Data · $9.99
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

const Stat = ({ icon, label, value }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5">
    <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500">{icon} {label}</p>
    <p className="mt-0.5 font-display text-lg font-bold text-white">{value}</p>
  </div>
);
